import React, { useState, useEffect } from "react";
import { interactionsAPI, salespersonsAPI, customersAPI } from "../services/api";
import SearchableSelect from "./SearchableSelect";

const getLocalDateString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const parseDateLocal = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const METHOD_OPTIONS = [
  { value: "Visited",    label: "Visited" },
  { value: "Phone Call", label: "Phone Call" },
  { value: "Message",    label: "Message" },
  { value: "Video Call", label: "Video Call" },
  { value: "Meeting",    label: "Meeting" },
];

const InteractionModal = ({ interaction, customerId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    contact_date:   getLocalDateString(),
    contact_method: "Visited",
    salesperson_id: "",
    notes:          "",
    scheduled_time: "",
    is_scheduled:   0,
  });
  const [selectedCustomerId, setSelectedCustomerId] = useState(customerId || "");
  const [customers,    setCustomers]    = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");
  const [isScheduled, setIsScheduled] = useState(false);

  useEffect(() => {
    fetchSalespersons();
    if (!customerId) fetchCustomers();
    if (interaction) {
      const contactDate = interaction.contact_date || getLocalDateString();
      const todayMidnight = new Date();
      todayMidnight.setHours(0, 0, 0, 0);
      const parsed = parseDateLocal(contactDate);
      const sched = interaction.is_scheduled === 1 || (parsed && parsed > todayMidnight);

      setFormData({
        contact_date:   contactDate,
        contact_method: interaction.contact_method || "Visited",
        salesperson_id: interaction.salesperson_id || "",
        notes:          interaction.notes          || "",
        scheduled_time: interaction.scheduled_time || "",
        is_scheduled:   interaction.is_scheduled   || 0,
      });
      setIsScheduled(sched);
      if (interaction.customer_id) setSelectedCustomerId(String(interaction.customer_id));
    }
  }, [interaction, customerId]);

  useEffect(() => {
    const parsed = parseDateLocal(formData.contact_date);
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);
    const isFuture = parsed ? parsed > todayMidnight : false;
    setIsScheduled(isFuture);
    setFormData(prev => ({ ...prev, is_scheduled: isFuture ? 1 : 0 }));
  }, [formData.contact_date]);

  const fetchSalespersons = async () => {
    try {
      const res = await salespersonsAPI.getAll();
      setSalespersons(res.data);
    } catch (err) {
      console.error("Failed to load salespersons:", err);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await customersAPI.getAll();
      setCustomers(res.data);
    } catch (err) {
      console.error("Failed to load customers:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const effectiveCustomerId = customerId || selectedCustomerId;
    if (!effectiveCustomerId) {
      setError("Please select a customer");
      setLoading(false);
      return;
    }

    try {
      const data = { ...formData, customer_id: effectiveCustomerId };
      if (interaction) {
        await interactionsAPI.update(interaction.id, data);
      } else {
        await interactionsAPI.create(data);
      }
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save interaction");
    } finally {
      setLoading(false);
    }
  };

  const setField = (name) => (value) =>
    setFormData(prev => ({ ...prev, [name]: value }));

  const today = getLocalDateString();

  const salespersonOptions = salespersons.map(s => ({
    value: String(s.id),
    label: s.name + (s.employee_id ? ` (${s.employee_id})` : ""),
  }));

  const customerOptions = customers.map(c => ({
    value: String(c.id),
    label: c.customer_name + (c.contact_number ? ` — ${c.contact_number}` : ""),
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <h3 className="text-lg font-semibold text-gray-900">
              {interaction ? "Edit Interaction" : "Log New Interaction"}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-5 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {isScheduled && (
              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>
                  <strong>Scheduled Interaction:</strong> This will create notifications for you and admins for the scheduled date.
                </span>
              </div>
            )}

            {/* Customer selector — only when not opened from a customer page */}
            {!customerId && !interaction && (
              <div>
                <label className="form-label">Customer *</label>
                <SearchableSelect
                  value={selectedCustomerId}
                  onChange={setSelectedCustomerId}
                  options={customerOptions}
                  placeholder="Please select customer"
                  disabled={loading}
                />
              </div>
            )}

            <div>
              <label htmlFor="contact_date" className="form-label">
                {isScheduled ? "Scheduled Date" : "Contact Date"} *
              </label>
              <input
                type="date"
                id="contact_date"
                name="contact_date"
                required
                className="form-input"
                value={formData.contact_date}
                onChange={e => setField("contact_date")(e.target.value)}
                disabled={loading}
                max={isScheduled ? undefined : today}
              />
              {isScheduled && (
                <p className="mt-1 text-xs text-blue-600">
                  Future date selected — this will be saved as a scheduled reminder
                </p>
              )}
            </div>

            <div>
              <label className="form-label">Contact Method *</label>
              <SearchableSelect
                value={formData.contact_method}
                onChange={setField("contact_method")}
                options={METHOD_OPTIONS}
                placeholder="Please select method"
                disabled={loading}
              />
            </div>

            {isScheduled && (
              <div>
                <label htmlFor="scheduled_time" className="form-label">Scheduled Time</label>
                <input
                  type="time"
                  id="scheduled_time"
                  name="scheduled_time"
                  className="form-input"
                  value={formData.scheduled_time}
                  onChange={e => setField("scheduled_time")(e.target.value)}
                  disabled={loading}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Optional — set a specific time for the scheduled interaction
                </p>
              </div>
            )}

            <div>
              <label className="form-label">Salesperson</label>
              <SearchableSelect
                value={String(formData.salesperson_id)}
                onChange={setField("salesperson_id")}
                options={salespersonOptions}
                placeholder="Please select salesperson (optional)"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="notes" className="form-label">Notes</label>
              <textarea
                id="notes"
                name="notes"
                rows={3}
                className="form-textarea"
                placeholder="Add notes about this interaction..."
                value={formData.notes}
                onChange={e => setField("notes")(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-4 bg-gray-50 rounded-b-xl flex flex-col sm:flex-row-reverse gap-3">
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full sm:w-auto"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="loading-spinner" /> Saving...
                </span>
              ) : isScheduled
                ? (interaction ? "Update Schedule" : "Schedule Interaction")
                : (interaction ? "Update Interaction" : "Log Interaction")}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn btn-secondary w-full sm:w-auto"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InteractionModal;
